import { put, call, select, fork, take } from "redux-saga/effects";
import { email } from "@hapi/address";
import {
  ADD_USER,
  ADD_USER_REQUEST,
  ADD_USER_FAIL,
  ADD_USER_SUCCESS,
  SAVE_USER,
  SAVE_USER_REQUEST,
  SAVE_USER_FAIL,
  SAVE_USER_SUCCESS,
  VALIDATE_FORM_ERROR,
  END_ACTION,
} from "./actionTypes";
import { loadUsers } from "./actionCreators";
import { load as authenticateUser } from "../auth/actionCreators";
import { SELECT_BETA_PROJECT } from "../_BETA/selections";
import { LOAD_AUTH_USER_SUCCESS } from "../auth/actionTypes";

export function validateForm(newUser, formType) {
  // Test validate return ok always
  const error = [];
  let ok = true;

  if (!newUser.user_email) {
    error.push("Email is required.");
  } else if (!email.isValid(newUser.user_email)) {
    error.push("Email is not valid.");
  }

  if (!newUser.user_fullname || newUser.user_fullname.trim().length === 0) {
    error.push("Full Name is required.");
  }

  if (!newUser.user_username || newUser.user_username.trim().length === 0) {
    error.push("Username is required.");
  }

  if (!newUser.user_password && formType === "CreateUser") {
    error.push("Password is required.");
  }

  if (!newUser.user_password_confirm && formType === "CreateUser") {
    error.push("Confirm Password is required.");
  }

  if (
    formType === "CreateUser" &&
    newUser.user_password !== newUser.user_password_confirm
  ) {
    error.push(
      "Password and Confirm Password do not match. Try re-entering both."
    );
  }

  if (!newUser.user_role) {
    error.push("Role is required.");
  }

  if (!newUser.user_group) {
    error.push("Group is required.");
  }

  if (error.length > 0) {
    ok = false;
  }

  return {
    ok,
    error,
  };
}

function* addUser(row, user) {
  const action = {
    types: [ADD_USER, ADD_USER_SUCCESS, ADD_USER_FAIL],
    promise: (client) =>
      client.post("/auth/register", {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: {
          admin_role: user.role,
        },
        data: {
          username: row.user_username,
          email: row.user_email,
          password: row.user_password,
          display_name: row.user_fullname,
          group: row.user_group.split(","), // Captain expects this list as an array
          role: row.user_role,
          updatedBy: user.id,
        },
      }),
  };

  yield put(action);
}

export function* saveUser(row, user) {
  const action = yield {
    types: [SAVE_USER, SAVE_USER_SUCCESS, SAVE_USER_FAIL],
    promise: (client) =>
      client.post(`/user/save/${row.user_id}`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        params: {
          role: user.role,
          username: row.user_username,
        },
        data: {
          display_name: row.user_fullname,
          role: row.user_role,
          group: row.user_group.split(","),
          updatedBy: user.id,
          email: row.user_email,
        },
      }),
  };
  yield put(action);
}

export function* executeUserAction(response, func, user, auth) {
  if (response.ok) {
    // call add user asynchronously

    yield fork(func, user, auth);
  } else {
    // Return errors in form - Better to use redux form
    yield put({ type: VALIDATE_FORM_ERROR, error: response.error });
  }
}

// Our watcher Saga:
// const getCreateUserFormValues = state => state.form.createUser.values
const getCreateUserFormValues = (state) => state.users.user_form_data;
const getAuthUser = (state) => state.auth.user;

export function* createUserFlow() {
  while (true) {
    // Wait for start of cycle action
    yield take(ADD_USER_REQUEST);

    // Get values from form - Better to use redux form
    const newUser = yield select(getCreateUserFormValues);
    const authUser = yield select(getAuthUser);

    // Validate - Better to do this with redux form
    const response = yield call(validateForm, newUser, "CreateUser", authUser);

    // Add User task
    yield fork(executeUserAction, response, addUser, newUser, authUser);

    // Wait for end of cycle action
    yield take([
      ADD_USER_SUCCESS,
      ADD_USER_FAIL,
      END_ACTION,
      VALIDATE_FORM_ERROR,
    ]);
  }
}

const getSaveUserFormValues = (state) => state.users.user_form_data;

export function* saveUserFlow() {
  while (true) {
    // Wait for start of cycle action
    yield take(SAVE_USER_REQUEST);

    // Get values from form - Better to use redux form
    const editedUser = yield select(getSaveUserFormValues);
    const authUser = yield select(getAuthUser);

    // Validate - Better to do this with redux form
    const response = yield call(validateForm, editedUser, "EditUser", authUser);

    // Save User task
    yield fork(executeUserAction, response, saveUser, editedUser, authUser);

    // Wait for end of cycle action
    const result = yield take([
      SAVE_USER_SUCCESS,
      SAVE_USER_FAIL,
      VALIDATE_FORM_ERROR,
    ]);

    // If the user update is successful:
    if (result.type === SAVE_USER_SUCCESS) {
      // Reauthenticate user to refresh portfolio
      yield put(authenticateUser(authUser));
      yield take(LOAD_AUTH_USER_SUCCESS);

      // Once reauthenticated, select updated user from store
      const updatedUser = yield select(getAuthUser);

      // Refresh user list
      yield put(loadUsers(updatedUser));

      // Retrieve current active project and check that it's valid
      // against the updated user portfolio
      const { activeProject } = yield select((state) => state.betaSelections);

      // If current active project is no longer valid, replace with a valid project
      // from updated user portfolio
      if (
        updatedUser.portfolio.projects[activeProject.project_pk] === undefined
      ) {
        yield put({
          type: SELECT_BETA_PROJECT,
          projectId: Object.keys(updatedUser.portfolio.projects)[0],
        });
      }
    }
  }
}
